import type { Engagement, Month, PeriodKey } from '../../types'
import { shareOf, shortMonth } from '../../utils'

export type Channel = 'onboarded' | 'app' | 'coaching' | 'workshops'
export type EngagementPoint = Engagement['series'][number]

// The coaching board counts people who have since left, so a count can pass
// the people enrolled now. Show that as a count, never as a share.
export const exceedsEnrolled = (count: number, enrolled: number): boolean => count > enrolled

// A share of enrolled for a bar or a line: never past 100%.
export const cappedShare = (count: number, enrolled: number): number => Math.min(100, shareOf(count, enrolled) ?? 0)

// 70 → "7 in 10", for a title that leads with it.
export const inTen = (share: number): string => {
  if (share >= 100) return 'All'
  const tenths = Math.round(share / 10)
  return tenths >= 10 ? 'Nearly all' : `${tenths} in 10`
}

// "Up from 18% in Feb": a channel's share at the start of the period against
// its latest. Null with fewer than two months to compare.
export const changeFrom = (points: EngagementPoint[], channel: Channel, enrolled: number): string | null => {
  if (points.length < 2) return null

  const first = points[0]
  const from = shareOf(first[channel], enrolled) ?? 0
  const to = shareOf(points[points.length - 1][channel], enrolled) ?? 0
  const month = shortMonth(first.month)

  if (to > from) return `Up from ${from}% in ${month}`
  if (to < from) return `Down from ${from}% in ${month}`
  return `No change since ${month}`
}

// The name at the end of each engagement line: "Onboarded 70%".
export const endLabel = (name: string, count: number, enrolled: number): string => {
  if (count === 0) return `${name}: none yet`
  if (exceedsEnrolled(count, enrolled)) return `${name}: above enrolled`
  return `${name} ${cappedShare(count, enrolled)}%`
}

// A share of enrolled in words: "41%", or "above enrolled" past 100%.
export const shareText = (count: number, enrolled: number): string =>
  exceedsEnrolled(count, enrolled) ? 'above enrolled' : `${cappedShare(count, enrolled)}%`

export type WorkshopState = 'no_attendance' | 'under_five' | 'shown'

// No attendance yet; every topic too small to show; or at least one shown.
export const workshopState = (workshops: Engagement['workshops']): WorkshopState => {
  if (workshops.attendees === 0) return 'no_attendance'

  const anyShown = workshops.topics.some((topic) => !topic.hidden) || (workshops.untagged_attendees ?? 0) > 0
  return anyShown ? 'shown' : 'under_five'
}

// Workshops ran before, but none in this period.
export const isEmptyPeriod = (workshops: Engagement['workshops'], period: PeriodKey): boolean =>
  period !== 'since_start' && workshops.sessions_held > 0 && workshops.periods[period].sessions_held === 0

// Line-end labels, as pixel heights, spread so no two sit closer than `gap`.
// Labels keep their top-to-bottom order and move as little as they can: a
// pass down pushes crowded labels apart, a pass up keeps them above `bottom`.
// A label with room around it stays where it is.
export const spreadLabels = (ys: number[], gap: number, bottom: number): number[] => {
  const order = ys.map((y, index) => ({ y, index })).sort((a, b) => a.y - b.y || a.index - b.index)
  const down = order.reduce<number[]>((spread, { y }, i) => [...spread, i === 0 ? y : Math.max(y, spread[i - 1] + gap)], [])
  const placed = [...down]
  for (let i = placed.length - 1; i >= 0; i -= 1) {
    placed[i] = i === placed.length - 1 ? Math.min(placed[i], bottom) : Math.min(placed[i], placed[i + 1] - gap)
  }
  const result: number[] = []
  order.forEach(({ index }, i) => {
    result[index] = placed[i]
  })
  return result
}

// From a start month to the latest captured one; nothing before the start is
// known. Workshops can start after the latest capture.
export const sinceStartMonths = (first: Month | null, series: { month: Month }[]): { month: Month }[] => {
  if (!first) return []

  const latest = series.length > 0 ? series[series.length - 1].month : first
  return [{ month: first }, { month: latest > first ? latest : first }]
}
